import {FormEvent, useEffect, useMemo, useState} from "react";
import {
  CreateUserRequest,
  UserAdministrationAudit,
  UserAdministrationUser,
  createUser,
  listAssignableRoles,
  listUserAudit,
  listUsers,
  replaceRoles,
  resetPassword,
  setUserEnabled,
  updateUser,
} from "../../api/userAdministration";
import {useAuth} from "../../auth/AuthContext";
import {PageHeader} from "../../components/PageHeader";

const ADMIN_ROLES = new Set([
  "factory-operations",
]);

type Mode = "none" | "create" | "edit" | "password" | "audit";

type FormState = {
  username: string;
  displayName: string;
  email: string;
  tenantId: string;
  company: string;
  businessUnit: string;
  roles: string[];
  initialPassword: string;
  temporaryPassword: boolean;
};

const emptyForm = (): FormState => ({
  username: "",
  displayName: "",
  email: "",
  tenantId: "",
  company: "",
  businessUnit: "",
  roles: ["workbench-viewer"],
  initialPassword: "",
  temporaryPassword: true,
});

export function UserAdministration() {
  const {authorizedFetch, user} = useAuth();

  const [users, setUsers] = useState<UserAdministrationUser[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("none");
  const [form, setForm] = useState<FormState>(emptyForm());
  const [audit, setAudit] = useState<UserAdministrationAudit[]>([]);
  const [password, setPassword] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState(true);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  const isAdmin =
    user?.roles.some(role => ADMIN_ROLES.has(role)) ?? false;

  const selected = useMemo(
    () => users.find(value => value.id === selectedId) ?? null,
    [users, selectedId],
  );

  useEffect(() => {
    const controller = new AbortController();

    void Promise.all([
      listUsers(authorizedFetch, controller.signal),
      listAssignableRoles(authorizedFetch, controller.signal),
    ])
      .then(([userValues, roleValues]) => {
        setUsers(userValues);
        setRoles([...roleValues].sort());
        setSelectedId(userValues[0]?.id ?? null);
      })
      .catch(reason => {
        if (!controller.signal.aborted) {
          setError(message(reason));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [authorizedFetch]);

  const refresh = async (preferredId?: string) => {
    const values = await listUsers(authorizedFetch);
    setUsers(values);

    if (preferredId && values.some(value => value.id === preferredId)) {
      setSelectedId(preferredId);
    } else if (!selectedId || !values.some(value => value.id === selectedId)) {
      setSelectedId(values[0]?.id ?? null);
    }
  };

  const beginCreate = () => {
    setError("");

    setForm({
      ...emptyForm(),
      tenantId: user?.tenant ?? "",
      company: user?.company ?? "",
      businessUnit: user?.businessUnit ?? "",
    });

    setMode("create");
  };

  const beginEdit = () => {
    if (!selected) return;

    setError("");

    setForm({
      username: selected.username,
      displayName: selected.displayName,
      email: selected.email,
      tenantId: selected.tenantId,
      company: selected.company,
      businessUnit: selected.businessUnit,
      roles: selected.roles,
      initialPassword: "",
      temporaryPassword: true,
    });

    setMode("edit");
  };

  const submitUser = async (event: FormEvent) => {
    event.preventDefault();
    setWorking(true);
    setError("");

    try {
      let updated: UserAdministrationUser;

      if (mode === "create") {
        const request: CreateUserRequest = {
          username: form.username.trim(),
          displayName: form.displayName.trim(),
          email: form.email.trim(),
          tenantId: form.tenantId,
          company: form.company.trim(),
          businessUnit: form.businessUnit.trim(),
          roles: form.roles,
          initialPassword: form.initialPassword,
          temporaryPassword: form.temporaryPassword,
        };

        updated = await createUser(authorizedFetch, request);
      } else {
        if (!selected) return;

        updated = await updateUser(authorizedFetch, selected.id, {
          displayName: form.displayName.trim(),
          email: form.email.trim(),
          tenantId: form.tenantId,
          company: form.company.trim(),
          businessUnit: form.businessUnit.trim(),
        });

        updated = await replaceRoles(authorizedFetch, updated.id, form.roles);
      }

      await refresh(updated.id);
      setSelectedId(updated.id);
      setMode("none");
    } catch (reason) {
      setError(message(reason));
    } finally {
      setWorking(false);
    }
  };

  const toggleEnabled = async () => {
    if (!selected) return;

    setWorking(true);
    setError("");

    try {
      const updated = await setUserEnabled(
        authorizedFetch,
        selected.id,
        !selected.enabled,
      );

      await refresh(updated.id);
    } catch (reason) {
      setError(message(reason));
    } finally {
      setWorking(false);
    }
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();

    if (!selected) return;

    setWorking(true);
    setError("");

    try {
      await resetPassword(
        authorizedFetch,
        selected.id,
        password,
        temporaryPassword,
      );

      setPassword("");
      setMode("none");
    } catch (reason) {
      setError(message(reason));
    } finally {
      setWorking(false);
    }
  };

  const openAudit = async () => {
    if (!selected) return;

    setWorking(true);
    setError("");

    try {
      setAudit(await listUserAudit(authorizedFetch, selected.id));
      setMode("audit");
    } catch (reason) {
      setError(message(reason));
    } finally {
      setWorking(false);
    }
  };

  return (
    <main className="live-ai-page">
      <PageHeader
        eyebrow="Administration / Users & Roles"
        title="Users & Roles"
        description="Manage named human access, scope and approved Software Factory roles."
        aside={
          isAdmin
            ? <button type="button" onClick={beginCreate}>Create user</button>
            : undefined
        }
      />

      <div className="authorization-note">
        <strong>Governed access</strong>
        <span>
          User administration does not change approval separation rules.
          Authors still cannot approve their own governed work.
        </span>
      </div>

      {error && <p className="separation-reason" role="alert">{error}</p>}

      {loading ? (
        <p role="status">Loading users…</p>
      ) : (
        <div className="ai-admin-layout">
          <section
            className="registry-table ai-admin-table"
            tabIndex={0}
            aria-label="Software Factory users"
          >
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Company</th>
                  <th>Business unit</th>
                  <th>Roles</th>
                </tr>
              </thead>
              <tbody>
                {users.map(value => (
                  <tr
                    key={value.id}
                    className={value.id === selectedId ? "selected" : ""}
                    onClick={() => {
                      setSelectedId(value.id);
                      setMode("none");
                    }}
                  >
                    <td>
                      <strong>{value.displayName}</strong>
                      <small>{value.username}</small>
                    </td>
                    <td>{value.enabled ? "Enabled" : "Disabled"}</td>
                    <td>{value.company}</td>
                    <td>{value.businessUnit}</td>
                    <td>{value.roles.join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <p>No users are visible in the current scope.</p>
            )}
          </section>

          <section className="ai-config-detail">
            {selected ? (
              <>
                <header>
                  <div>
                    <small>User</small>
                    <h2>{selected.displayName}</h2>
                    <code>{selected.username}</code>
                  </div>
                  <span>{selected.enabled ? "Enabled" : "Disabled"}</span>
                </header>

                <dl className="user-admin-details">
                  <div><dt>Email</dt><dd>{selected.email || "—"}</dd></div>
                  <div><dt>Tenant</dt><dd>{selected.tenantId}</dd></div>
                  <div><dt>Company</dt><dd>{selected.company}</dd></div>
                  <div><dt>Business unit</dt><dd>{selected.businessUnit}</dd></div>
                  <div><dt>Roles</dt><dd>{selected.roles.join(", ") || "—"}</dd></div>
                </dl>

                {isAdmin && mode === "none" && (
                  <div className="ai-detail-actions">
                    <button type="button" onClick={beginEdit}>Edit user</button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={toggleEnabled}
                      disabled={working}
                    >
                      {selected.enabled ? "Disable user" : "Enable user"}
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        setPassword("");
                        setTemporaryPassword(true);
                        setMode("password");
                      }}
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={openAudit}
                    >
                      View audit
                    </button>
                  </div>
                )}

                {(mode === "create" || mode === "edit") && (
                  <UserForm
                    mode={mode}
                    form={form}
                    roles={roles}
                    working={working}
                    onChange={setForm}
                    onSubmit={submitUser}
                    onCancel={() => setMode("none")}
                  />
                )}

                {mode === "password" && (
                  <form className="ai-config-form" onSubmit={submitPassword}>
                    <label>
                      New password
                      <input
                        type="password"
                        minLength={12}
                        required
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                      />
                    </label>

                    <label className="immutable-control">
                      <input
                        type="checkbox"
                        checked={temporaryPassword}
                        onChange={event =>
                          setTemporaryPassword(event.target.checked)}
                      />
                      Require password change at next login
                    </label>

                    <div className="ai-detail-actions">
                      <button type="submit" disabled={working}>
                        Reset password
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setMode("none")}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {mode === "audit" && (
                  <div className="version-history">
                    <h3>Administration audit</h3>
                    <ul>
                      {audit.map(item => (
                        <li key={item.id}>
                          <span>{item.action}</span>
                          <span>{item.actorUsername}</span>
                          <time>{new Date(item.occurredAt).toLocaleString()}</time>
                        </li>
                      ))}
                    </ul>

                    {audit.length === 0 && <p>No audit events recorded.</p>}

                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setMode("none")}
                    >
                      Close audit
                    </button>
                  </div>
                )}
              </>
            ) : mode === "create" ? (
              <UserForm
                mode="create"
                form={form}
                roles={roles}
                working={working}
                onChange={setForm}
                onSubmit={submitUser}
                onCancel={() => setMode("none")}
              />
            ) : (
              <p>Select a user or create a new user.</p>
            )}
          </section>
        </div>
      )}
    </main>
  );
}

function UserForm({
  mode,
  form,
  roles,
  working,
  onChange,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  form: FormState;
  roles: string[];
  working: boolean;
  onChange: (value: FormState) => void;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
}) {
  const role = (value: string, checked: boolean) => {
    const next = checked
      ? [...new Set([...form.roles, value])]
      : form.roles.filter(current => current !== value);

    onChange({...form, roles: next});
  };

  return (
    <form className="ai-config-form" onSubmit={onSubmit}>
      {mode === "create" && (
        <label>
          Username
          <input
            required
            value={form.username}
            onChange={event =>
              onChange({...form, username: event.target.value})}
          />
        </label>
      )}

      <label>
        Display name
        <input
          required
          value={form.displayName}
          onChange={event =>
            onChange({...form, displayName: event.target.value})}
        />
      </label>

      <label>
        Email
        <input
          type="email"
          value={form.email}
          onChange={event =>
            onChange({...form, email: event.target.value})}
        />
      </label>

      <label>
        Tenant
        <input value={form.tenantId} readOnly />
      </label>

      <label>
        Company
        <input value={form.company} readOnly />
      </label>

      <label>
        Business unit
        <input value={form.businessUnit} readOnly />
      </label>

      <fieldset className="tool-selector">
        <legend>Software Factory roles</legend>
        {roles.map(value => (
          <label key={value}>
            <input
              type="checkbox"
              checked={form.roles.includes(value)}
              onChange={event => role(value, event.target.checked)}
            />
            <span>{value}</span>
          </label>
        ))}
      </fieldset>

      {mode === "create" && (
        <>
          <label>
            Initial password
            <input
              type="password"
              minLength={12}
              required
              value={form.initialPassword}
              onChange={event =>
                onChange({...form, initialPassword: event.target.value})}
            />
          </label>

          <label className="immutable-control">
            <input
              type="checkbox"
              checked={form.temporaryPassword}
              onChange={event =>
                onChange({...form, temporaryPassword: event.target.checked})}
            />
            Require password change at first login
          </label>
        </>
      )}

      <div className="ai-detail-actions">
        <button type="submit" disabled={working}>
          {mode === "create" ? "Create user" : "Save changes"}
        </button>
        <button type="button" className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function message(reason: unknown) {
  return reason instanceof Error
    ? reason.message
    : "User administration operation failed.";
}
